import { useEffect, useRef, useState } from "react";
import { Logo } from "./Logo";
import { api } from "../lib/api";
import { ShieldCheck, Sparkles, Lock } from "lucide-react";

interface GoogleCredentialResponse { credential: string }
interface GoogleAccountsId {
  initialize: (cfg: { client_id: string; callback: (r: GoogleCredentialResponse) => void; auto_select?: boolean; itp_support?: boolean }) => void;
  renderButton: (el: HTMLElement, cfg: { theme: string; size: string; type: string; shape: string; text: string; logo_alignment: string; width: number }) => void;
  prompt: () => void;
}
interface TurnstileApi {
  render: (el: HTMLElement, cfg: { sitekey: string; theme: string; size: string; callback: (token: string) => void }) => string;
}
declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } };
    turnstile?: TurnstileApi;
  }
}

interface LoginProps {
  googleClientId: string;
  turnstileSiteKey: string;
  onSuccess: (token: string) => void;
}

export function Login({ googleClientId, turnstileSiteKey, onSuccess }: LoginProps) {
  const gbtnRef = useRef<HTMLDivElement>(null);
  const tsRef = useRef<HTMLDivElement>(null);
  const [tsToken, setTsToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Render Google button when SDK is ready.
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (window.google?.accounts?.id && gbtnRef.current && googleClientId) {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async (resp: GoogleCredentialResponse) => {
            try {
              setLoading(true);
              setError(null);
              const r = await api.loginNative(resp.credential, tsToken || undefined);
              api.setToken(r.token);
              onSuccess(r.token);
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            } finally {
              setLoading(false);
            }
          },
          itp_support: true,
        });
        window.google.accounts.id.renderButton(gbtnRef.current, {
          theme: "filled_black",
          size: "large",
          type: "standard",
          shape: "pill",
          text: "signin_with",
          logo_alignment: "left",
          width: 320,
        });
      } else {
        setTimeout(tick, 200);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [googleClientId, tsToken, onSuccess]);

  // Render Turnstile when SDK is ready.
  useEffect(() => {
    if (!turnstileSiteKey) return;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (window.turnstile && tsRef.current) {
        window.turnstile.render(tsRef.current, {
          sitekey: turnstileSiteKey,
          theme: "dark",
          size: "flexible",
          callback: (t: string) => setTsToken(t),
        });
      } else {
        setTimeout(tick, 200);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [turnstileSiteKey]);

  return (
    <div className="min-h-dvh grid place-items-center p-6">
      <div className="glass-strong w-full max-w-md p-8 animate-slide-up">
        <div className="flex items-center justify-between mb-6">
          <Logo size="lg" />
          <span className="pill"><Sparkles size={12} /> beta</span>
        </div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mb-2 text-balance">Войдите в Glass</h1>
        <p className="text-ink-300 mb-6 leading-relaxed">
          Современный мессенджер с приватностью, групповыми звонками и Audio Rooms. Авторизация только через настоящий Google-аккаунт — это блокирует ботов.
        </p>

        <div className="space-y-3 mb-6">
          <div className="glass-row p-3 flex items-start gap-3">
            <ShieldCheck className="shrink-0 mt-0.5 text-accent-300" size={18} />
            <div className="text-sm text-ink-200">Cloudflare Turnstile + анти-бот эвристики (1–99 шкала риска).</div>
          </div>
          <div className="glass-row p-3 flex items-start gap-3">
            <Lock className="shrink-0 mt-0.5 text-accent-300" size={18} />
            <div className="text-sm text-ink-200">Stealth-режим, исчезающие сообщения, lobby-приёмная для встреч.</div>
          </div>
        </div>

        {turnstileSiteKey && (
          <div className="mb-4 flex justify-center">
            <div ref={tsRef} />
          </div>
        )}

        <div className="flex justify-center">
          <div ref={gbtnRef} />
        </div>

        {!googleClientId && (
          <div className="mt-4 text-sm text-amber-300">
            Google Client ID не настроен. Свяжитесь с админом инстанса.
          </div>
        )}
        {loading && <div className="mt-4 text-center text-ink-300">Вход…</div>}
        {error && <div className="mt-4 text-sm text-red-300">{error}</div>}
        <div className="mt-6 text-xs text-ink-400 leading-relaxed text-center">
          Продолжая, вы соглашаетесь с прозрачной политикой конфиденциальности. Боты не пройдут.
        </div>
      </div>
    </div>
  );
}
